'use client'

import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import {
  IconChevronRight,
  IconCreditCard,
  IconLayoutDashboard,
  IconLogout,
  IconMenu2,
  IconServer,
  IconWallet,
  IconX,
} from '@tabler/icons-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { BrandMark } from '@/components/ui/brand-mark'
import { branding } from '@/lib/branding.config'
import { cn } from '@/lib/utils'

export interface NavbarItem {
  label: string
  href: string
  icon?: React.ReactNode
}

export interface NavbarProps {
  logoHref?: string
  navigationLinks?: NavbarItem[]
  showWallet?: boolean
  walletBalance?: string
  walletHref?: string
  showAuth?: boolean
  isAuthenticated?: boolean
  user?: {
    name?: string
    email?: string
    image?: string
  }
  onSignOut?: () => void
  signInHref?: string
  className?: string
}

const DEFAULT_NAV_LINKS: NavbarItem[] = [
  { label: 'Cloud PCs', href: '/products' },
  { label: 'My Machines', href: '/machines' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Requirements', href: '/requirements' },
  { label: 'Help', href: '/help' },
]

const pad = (n: number) => String(n).padStart(2, '0')

const menuItemClass =
  'flex cursor-pointer items-center gap-2.5 rounded-none px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-foreground focus:bg-foreground focus:text-background'

export function NavbarTwoRow({
  logoHref = '/',
  navigationLinks = DEFAULT_NAV_LINKS,
  showWallet = false,
  walletBalance = '€0.00',
  walletHref = '/wallet',
  showAuth = true,
  isAuthenticated = false,
  user,
  onSignOut,
  signInHref = '/sign-in',
  className,
}: NavbarProps) {
  const pathname = usePathname()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  const isActiveLink = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname?.startsWith(href)
  }

  return (
    <nav className={cn('sticky top-0 z-50 w-full bg-background', className)}>
      {/* Utility row: the filing header */}
      <div className="border-b border-foreground/20">
        <div className="flex h-7 items-center justify-between gap-4 px-5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground md:px-6">
          <span className="truncate">
            <span className="font-bold text-foreground">{branding.name}</span> {'//'} Index
          </span>
          <span className="hidden truncate lg:block">
            Prepaid wallet {'//'} Metered per minute {'//'} Remote Desktop
          </span>
          {showWallet && isAuthenticated ? (
            <Link
              href={walletHref}
              className="flex shrink-0 items-center gap-1.5 font-bold text-foreground hover:underline"
            >
              <IconWallet className="h-3 w-3" stroke={2.5} />
              Wallet <span className="tabular-nums">{walletBalance}</span>
            </Link>
          ) : showAuth && !isAuthenticated ? (
            <Link href={signInHref} className="shrink-0 font-bold text-foreground hover:underline">
              {'>>>'} Sign in
            </Link>
          ) : (
            <span aria-hidden />
          )}
        </div>
      </div>

      {/* Primary row: hard split, logo left, index right */}
      <div className="border-b-2 border-foreground">
        <div className="flex h-14 items-stretch justify-between pl-5 md:pl-6">
          <Link href={logoHref} className="group flex items-center pr-6">
            <BrandMark size="sm" />
          </Link>

          <div className="flex items-stretch">
            {/* Desktop Navigation */}
            <div className="hidden items-stretch lg:flex">
              {navigationLinks.map((link, index) => {
                const isActive = isActiveLink(link.href)
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-2 border-l border-foreground/20 px-4 text-xs font-bold uppercase tracking-[0.08em] transition-colors',
                      isActive
                        ? 'bg-foreground text-background'
                        : 'text-foreground hover:bg-secondary'
                    )}
                  >
                    <span className={cn('tabular-nums', isActive ? 'text-background/70' : 'text-muted-foreground')}>
                      {pad(index + 1)}
                    </span>
                    {link.label}
                  </Link>
                )
              })}
            </div>

            {/* Auth Section */}
            {showAuth && (
              <>
                {isAuthenticated && user ? (
                  <div className="flex items-center border-l border-foreground/20 px-4">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <button
                          aria-label="Account menu"
                          className={cn(
                            'flex h-8 w-8 items-center justify-center overflow-hidden rounded-none border-2 border-foreground transition-colors',
                            'bg-background text-foreground hover:bg-foreground hover:text-background',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                          )}
                        >
                          {user.image ? (
                            <Image
                              src={user.image}
                              alt={user.name || 'User'}
                              width={32}
                              height={32}
                              unoptimized
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-bold">
                              {user.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                        </button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent
                        align="end"
                        sideOffset={10}
                        className="w-72 overflow-hidden rounded-none border-2 border-foreground bg-popover p-0 text-popover-foreground shadow-none"
                      >
                        {/* User Header */}
                        <div className="border-b border-foreground/20 px-4 py-3">
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            [ Account on file ]
                          </p>
                          <p className="mt-1.5 font-heading text-base font-bold text-foreground">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>

                        {/* Wallet line */}
                        {showWallet && (
                          <Link
                            href={walletHref}
                            className="flex items-baseline gap-2 border-b border-foreground/20 px-4 py-3 text-foreground transition-colors hover:bg-secondary"
                          >
                            <span className="text-xs font-bold uppercase tracking-[0.12em]">Balance</span>
                            <span aria-hidden className="flex-1 translate-y-[-3px] border-b border-dotted border-current opacity-40" />
                            <span className="font-heading text-lg font-bold tabular-nums">{walletBalance}</span>
                          </Link>
                        )}

                        {/* Menu Items */}
                        <div className="p-1.5">
                          <DropdownMenuItem asChild className={menuItemClass}>
                            <Link href="/dashboard">
                              <IconLayoutDashboard className="h-4 w-4" />
                              <span>Dashboard</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className={menuItemClass}>
                            <Link href="/machines">
                              <IconServer className="h-4 w-4" />
                              <span>My Machines</span>
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className={menuItemClass}>
                            <Link href="/wallet">
                              <IconCreditCard className="h-4 w-4" />
                              <span>Billing</span>
                            </Link>
                          </DropdownMenuItem>
                        </div>

                        <DropdownMenuSeparator className="my-0 bg-foreground/20" />

                        <div className="p-1.5">
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={onSignOut}
                            className="flex cursor-pointer items-center gap-2.5 rounded-none px-3 py-2 text-xs font-bold uppercase tracking-[0.12em]"
                          >
                            <IconLogout className="h-4 w-4" />
                            <span>Sign out</span>
                          </DropdownMenuItem>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : (
                  <Link
                    href="/sign-up"
                    className="hidden items-center bg-primary px-5 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-primary/90 sm:flex"
                  >
                    Open account
                  </Link>
                )}
              </>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              className={cn(
                'flex w-14 items-center justify-center border-l-2 border-foreground transition-colors lg:hidden',
                isMobileMenuOpen
                  ? 'bg-foreground text-background'
                  : 'text-foreground hover:bg-secondary'
              )}
            >
              {isMobileMenuOpen ? <IconX className="h-5 w-5" /> : <IconMenu2 className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu: full-screen slab */}
      {isMobileMenuOpen && (
        <div className="fixed inset-x-0 bottom-0 top-[87px] z-40 overflow-y-auto bg-background lg:hidden">
          <p className="border-b border-foreground/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            [ Index ] {'//'} {navigationLinks.length} entries
          </p>
          <div>
            {navigationLinks.map((link, index) => {
              const isActive = isActiveLink(link.href)
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-baseline gap-4 border-b-2 border-foreground px-5 py-4 transition-colors active:bg-foreground active:text-background',
                    isActive ? 'bg-foreground text-background' : 'text-foreground'
                  )}
                >
                  <span className="w-6 shrink-0 text-xs font-bold tabular-nums">{pad(index + 1)}</span>
                  <span className="font-heading text-3xl font-bold uppercase leading-none tracking-tight">
                    {link.label}
                  </span>
                </Link>
              )
            })}

            {/* Wallet - Mobile */}
            {showWallet && isAuthenticated && (
              <Link
                href={walletHref}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-baseline gap-3 border-b-2 border-foreground px-5 py-4 text-foreground transition-colors active:bg-foreground active:text-background"
              >
                <IconWallet className="h-4 w-4 shrink-0 self-center" stroke={2.5} />
                <span className="text-xs font-bold uppercase tracking-[0.14em]">Wallet balance</span>
                <span aria-hidden className="flex-1 translate-y-[-3px] border-b border-dotted border-current opacity-40" />
                <span className="font-heading text-2xl font-bold tabular-nums">{walletBalance}</span>
              </Link>
            )}

            {/* My Machines - Quick access for logged in users */}
            {isAuthenticated && (
              <Link
                href="/machines"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-between border-b-2 border-foreground px-5 py-4 text-foreground transition-colors active:bg-foreground active:text-background"
              >
                <span className="flex items-center gap-3">
                  <IconServer className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-[0.14em]">Machine panel</span>
                </span>
                <IconChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Auth CTA - Mobile */}
          {showAuth && !isAuthenticated && (
            <div className="grid grid-cols-2">
              <Link
                href={signInHref}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex h-14 items-center justify-center border-b-2 border-r-2 border-foreground text-xs font-bold uppercase tracking-[0.14em] text-foreground active:bg-foreground active:text-background"
              >
                Sign in
              </Link>
              <Link
                href="/sign-up"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex h-14 items-center justify-center border-b-2 border-foreground bg-primary text-xs font-bold uppercase tracking-[0.14em] text-primary-foreground"
              >
                Open account
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  )
}
